// components/slides/SlidesRunStatus.tsx
'use client';

import { CircleAlert, CircleCheck, CirclePause, LoaderCircle } from 'lucide-react';
import { useSlidesStore } from './slidesStore';

export function SlidesRunStatus({ status, text, detail, unsavedDetail, showSaveRecovery, reviewTour, progress }: {
  status: string; text: string; detail: string; unsavedDetail?: string; showSaveRecovery?: boolean; reviewTour?: boolean;
  /** 0..100, drawn as a thin bar under the line. */
  progress?: number;
}) {
  const projectId = useSlidesStore(s => s.activeProjectId);
  const conflict = useSlidesStore(s => s.activeProjectId ? s.saveStates[s.activeProjectId]?.conflict : false);
  const busy = status === 'running' || status === 'saving';
  const Icon = busy ? LoaderCircle : status === 'completed' ? CircleCheck : status === 'stopped' ? CirclePause : CircleAlert;
  return (
    // ONE quiet line above the composer, ChatGPT's way (owner, 2026-09-28: two stacked two-line
    // blocks crowded the conversation): the state, then its detail in grey, cut to the line; the
    // whole sentence stays on hover and for screen readers.
    // px-3: the icon and the bar start where the chip labels below start.
    <div className="mb-1.5 px-3" data-slides-tour={reviewTour ? 'status' : undefined} data-slides-tour-project={reviewTour ? projectId : undefined}>
      <div role="status" aria-live="polite" aria-atomic="true" title={[text, detail, unsavedDetail].filter(Boolean).join('. ')}
        className="flex min-w-0 items-center gap-2 text-[#AFAFAF]" style={{ fontSize: 13, lineHeight: '18px' }}>
        <Icon aria-hidden="true" className={`h-3.5 w-3.5 shrink-0 ${busy ? 'motion-safe:animate-spin' : 'text-[#AFAFAF]'}`} />
        <span className="min-w-0 truncate" style={{ fontSize: 'inherit' }}>
          <span className="text-[#ECECEC]" style={{ fontSize: 'inherit' }}>{text}</span>
          <span style={{ fontSize: 'inherit' }}> · {detail}</span>
          {unsavedDetail && <span style={{ fontSize: 'inherit' }}> · {unsavedDetail}</span>}
        </span>
        {progress !== undefined && <span className="ml-auto shrink-0 tabular-nums text-[#ECECEC]" style={{ fontSize: 'inherit' }}>{progress.toFixed(1)}%</span>}
      </div>
      {progress !== undefined && (
        <div role="progressbar" aria-label="Review progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)}
          className="mt-1.5 h-[3px] w-full overflow-hidden rounded-full bg-[#262626]">
          <div className="h-full rounded-full bg-[#ECECEC] transition-[width] duration-1000 ease-linear" style={{ width: `${progress}%` }} />
        </div>
      )}
      {showSaveRecovery && !conflict && <button type="button" className="mt-1 rounded-full px-2.5 py-1 text-[#ECECEC] hover:bg-white/[0.08]" style={{ fontSize: 13 }}
        onClick={() => { const s = useSlidesStore.getState(); if (s.activeProjectId) void s.saveDeckNow(s.activeProjectId); }}>Retry save</button>}
      {showSaveRecovery && conflict && <p className="mt-1 text-[11px] text-[#A0A0A0]">Another version was saved. Your local changes have not been overwritten.</p>}
    </div>
  );
}
